import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

const loadingMessages = [
    { message: "Analyzing responses and dependencies...", duration: 10 },
    { message: "Generating executive summary...", duration: 10 },
    { message: "Creating PDF document...", duration: 10 }
];

const WaveAnimation = ({ progress }) => {
    return (
        <div className="absolute inset-0 overflow-hidden">
            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/50" />
            
            {/* Wave container */}
            <div 
                className="absolute bottom-0 left-0 right-0 transition-all duration-1000"
                style={{ 
                    height: `${progress}%`
                }}
            >
                {/* Filled background */}
                <div 
                    className="absolute inset-0"
                    style={{
                        background: `linear-gradient(
                            180deg, 
                            rgba(59, 130, 246, 0.3) 0%,
                            rgba(59, 130, 246, 0.4) 100%
                        )`
                    }}
                />
                
                {/* Wave top */}
                <div className="absolute -top-[2px] left-0 right-0 h-[50px] animate-wave" 
                    style={{
                        background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' fill='rgba(59, 130, 246, 0.4)'/%3E%3C/svg%3E") repeat-x`,
                        backgroundSize: '1200px 100%',
                        transform: 'rotate(180deg)',
                    }}
                />
                <div className="absolute -top-[2px] left-0 right-0 h-[50px] animate-wave-reverse" 
                    style={{
                        background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 120' preserveAspectRatio='none'%3E%3Cpath d='M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z' fill='rgba(59, 130, 246, 0.3)'/%3E%3C/svg%3E") repeat-x`,
                        backgroundSize: '1200px 100%',
                        transform: 'rotate(180deg)',
                    }}
                />
            </div>
        </div>
    );
};

const ReportLoadingDialog = ({ isOpen, onComplete, isReportReady }) => {
    const [timeLeft, setTimeLeft] = useState(30);
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [messageTimeLeft, setMessageTimeLeft] = useState(loadingMessages[0].duration);

    useEffect(() => {
        if (isReportReady) {
            onComplete?.();
            return;
        }

        if (!isOpen) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        const messageTimer = setInterval(() => {
            setMessageTimeLeft(prev => {
                if (prev <= 1) {
                    setCurrentMessageIndex(current => {
                        const nextIndex = current + 1;
                        if (nextIndex < loadingMessages.length) {
                            setMessageTimeLeft(loadingMessages[nextIndex].duration);
                            return nextIndex;
                        }
                        return current;
                    });
                    return loadingMessages[currentMessageIndex].duration;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            clearInterval(timer);
            clearInterval(messageTimer);
        };
    }, [isOpen, currentMessageIndex, isReportReady, onComplete]);

    useEffect(() => {
        if (timeLeft === 0) {
            onComplete?.();
        }
    }, [timeLeft, onComplete]);

    if (!isOpen) return null;

    const currentMessage = loadingMessages[currentMessageIndex].message;
    const progressPercent = ((30 - timeLeft) / 30) * 100;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <WaveAnimation progress={progressPercent} />
            
            <div className="relative bg-white/90 backdrop-blur-sm p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
                <div className="flex items-center gap-3 mb-6">
                    <Clock className="w-6 h-6 text-blue-600 animate-pulse" />
                    <h2 className="text-xl font-semibold">Generating Report</h2>
                </div>

                {/* Current task */}
                <div className="mb-4">
                    <p className="text-lg text-gray-700">{currentMessage}</p>
                    <p className="text-sm text-gray-500 mt-1">
                        Time remaining: {timeLeft} seconds
                    </p>
                </div>

                {/* All steps with status */}
                <div className="space-y-2">
                    {loadingMessages.map((msg, idx) => (
                        <div 
                            key={idx} 
                            className={`flex items-center gap-2 ${
                                idx === currentMessageIndex ? 'text-blue-600' :
                                idx < currentMessageIndex ? 'text-green-600' : 'text-gray-400'
                            }`}
                        >
                            <div className={`w-2 h-2 rounded-full ${
                                idx === currentMessageIndex ? 'bg-blue-600 animate-pulse' :
                                idx < currentMessageIndex ? 'bg-green-600' : 'bg-gray-400'
                            }`} />
                            <span className="text-sm">{msg.message}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ReportLoadingDialog;